import React from 'react';
import { motion } from 'framer-motion';
import { EASE_OUT } from '@/lib/motion';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';

export interface KPICardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  loading?: boolean;
  className?: string;
  'data-testid'?: string;
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  icon,
  trend,
  loading = false,
  className = '',
  'data-testid': testId = 'kpi-card',
}) => {
  const { theme } = useTheme();

  return (
    <motion.div
      data-testid={testId}
      className={`card p-6 ${className}`}
      transition={{ duration: 0.25, ease: EASE_OUT }}
    >
      <phantom-ui {...phantomProps(theme === 'dark', loading)}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="body-subtitle font-semibold text-text-primary mb-2">{title}</p>
            <p className="heading-2 font-bold text-text-primary" data-testid={`${testId}-value`}>
              {value ?? '---'}
            </p>
            {trend && (
              <div
                className={`mt-2 flex items-center gap-1 text-sm ${
                  trend.isPositive ? 'text-success-dark' : 'text-error-dark'
                }`}
                data-testid={`${testId}-trend`}
              >
                <span>{trend.isPositive ? '↑' : '↓'}</span>
                <span>{Math.abs(trend.value)}%</span>
              </div>
            )}
          </div>
          {icon && (
            <div
              className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary-light"
              data-testid={`${testId}-icon`}
            >
              {icon}
            </div>
          )}
        </div>
      </phantom-ui>
    </motion.div>
  );
};

export default KPICard;
